import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import SaveButton from '../components/SaveButton';
import ShareButton from '../components/ShareButton';
import IuteInstalments from '../components/IuteInstalments';
import { iuteSku } from '../utils/iutepay';
import { fitToFrame } from '../utils/fitImage';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getImageUrl } from '../utils/imageUrl';
import { trackEvent } from '../utils/analytics';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import { formatPrice } from '../utils/format';
import { ProductCard, Badge, Button, Skeleton } from '../components/primitives';
import SectionRenderer from '../components/sections/SectionRenderer';
import { HOME_SECTION_REGISTRY } from '../components/sections/homeSectionRegistry';
import './ProductDetail.css';
import { ageLabel } from '../utils/ageLabel';

function isVideo(url) {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
}

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, siteSettings } = useCart();
  const { maxItemQuantity, currency: ctxCurrency, currencyLabel, returnsEnabled, returnsWindowDays, featureAgeRecommendations, storeName, storeEmail, storePhone, storeAddress, storeNipt, whatsAppPhone } = useSiteSettings();
  const { t, i18n } = useTranslation();
  const maxQty = parseInt(maxItemQuantity) || 99;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const primaryCtaRef = React.useRef(null);
  const [stickyBarVisible, setStickyBarVisible] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, averageRating: 0 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ authorName: '', rating: 5, title: '', body: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistMessage, setWaitlistMessage] = useState(null);
  const [resourceTemplate, setResourceTemplate] = useState(null);
  const isEditorPreview = new URLSearchParams(window.location.search).get('themePreview') === '1';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/api/products/${slug}`);
        setProduct(response.data);
        setSelectedOptions({});
        api.get(`/api/themes/templates/${response.data.themeTemplateKey || 'product.default'}`)
          .then((templateResponse) => setResourceTemplate(templateResponse.data))
          .catch(() => setResourceTemplate(null));
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, i18n.language]);

  useEffect(() => {
    if (!isEditorPreview) return undefined;
    const handleLayoutPreview = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'STOREFRONT_LAYOUT_PREVIEW') return;
      setResourceTemplate(event.data.payload);
    };
    window.addEventListener('message', handleLayoutPreview);
    return () => window.removeEventListener('message', handleLayoutPreview);
  }, [isEditorPreview]);

  useEffect(() => {
    try {
      const token = localStorage.getItem('customer_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.email) setWaitlistEmail(payload.email);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!product?.id) return;

    // GA4 view_item — fires when a PDP first resolves, not on every
    // render. The product.id dependency ensures it fires once per
    // distinct product the user opens, even within an SPA session.
    trackEvent('view_item', {
      currency: ctxCurrency || 'USD',
      value: parseFloat(product.price) || 0,
      items: [{
        item_id: product.sku || String(product.id),
        product_id: product.id,
        item_name: product.name,
        item_category: product.category?.name,
        price: parseFloat(product.price) || 0,
        quantity: 1,
      }],
    });

    api.get(`/api/products/${product.id}/related?lang=${i18n.language}`)
      .then(res => setRelatedProducts(res.data.products || []))
      .catch(() => {});
    api.get(`/api/products/${product.id}/reviews`)
      .then(res => {
        setReviews(res.data.reviews || []);
        setReviewStats({ total: res.data.total || 0, averageRating: res.data.averageRating || 0 });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, i18n.language]);

  useEffect(() => {
    if (!product?.id) return;
    try {
      const key = 'recently_viewed';
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      const filtered = stored.filter(p => p.id !== product.id);
      filtered.unshift({ id: product.id, name: product.name, slug: product.slug, price: product.price, images: product.images });
      const trimmed = filtered.slice(0, 10);
      localStorage.setItem(key, JSON.stringify(trimmed));
      setRecentlyViewed(trimmed.filter(p => p.id !== product.id).slice(0, 4));
    } catch {}
  }, [product]);

  // The product's looping clip (if any) leads the gallery, before the photos.
  const media = [
    ...(product?.animationUrl ? [product.animationUrl] : []),
    ...(product?.images?.length > 0 ? product.images : []),
  ];

  // Mobile-only sticky buy bar — visible only when the desktop CTA
  // has scrolled out of view. Uses IntersectionObserver because the
  // viewport-based scroll math gets fiddly across PDP layout breakpoints.
  useEffect(() => {
    const target = primaryCtaRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStickyBarVisible(!entry.isIntersecting),
      { rootMargin: '-100px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [product, loading]);

  const goTo = useCallback((idx) => {
    if (media.length === 0) return;
    setSelectedIndex(((idx % media.length) + media.length) % media.length);
  }, [media.length]);

  const goPrev = useCallback(() => goTo(selectedIndex - 1), [goTo, selectedIndex]);
  const goNext = useCallback(() => goTo(selectedIndex + 1), [goTo, selectedIndex]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistSubmitting(true);
    setWaitlistMessage(null);
    try {
      await api.post(`/api/products/${product.id}/waitlist`, { email: waitlistEmail.trim() });
      setWaitlistMessage({ type: 'success', text: t('productDetail.waitlistSuccess', "You'll be notified when this product is available!") });
    } catch (err) {
      setWaitlistMessage({ type: 'error', text: err.response?.data?.error || t('productDetail.waitlistError', 'Failed to subscribe.') });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.authorName.trim()) return;
    setSubmittingReview(true);
    try {
      await api.post(`/api/products/${product.id}/reviews`, reviewForm);
      setShowReviewForm(false);
      setReviewForm({ authorName: '', rating: 5, title: '', body: '' });
      const res = await api.get(`/api/products/${product.id}/reviews`);
      setReviews(res.data.reviews || []);
      setReviewStats({ total: res.data.total || 0, averageRating: res.data.averageRating || 0 });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review.');
    } finally {
      setSubmittingReview(false);
    }
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, goPrev, goNext]);

  const getStockDisplay = (status) => {
    if (status === 'in_stock') return { className: 'in-stock', text: t('productDetail.inStock', 'In Stock') };
    if (status === 'low_stock') return { className: 'low-stock', text: t('productDetail.lowStock', 'Low Stock') };
    return { className: 'out-of-stock', text: t('productDetail.outOfStock', 'Out of Stock') };
  };

  if (loading) {
    return (
      <div className="product-detail-page" aria-busy="true">
        <div className="container">
          <div className="breadcrumb" aria-hidden="true">
            <Skeleton.Line width="280px" />
          </div>
          <div className="product-detail-layout">
            <div className="product-gallery">
              <Skeleton.Block aspectRatio="1 / 1" rounded="md" />
              <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton.Block key={i} width="64px" height="64px" rounded="md" />
                ))}
              </div>
            </div>
            <div className="product-info">
              <Skeleton.Line width="50%" />
              <Skeleton.Block height="2.5rem" rounded="md" style={{ marginTop: 'var(--space-3)', maxWidth: '440px' }} />
              <Skeleton.Block height="3rem" width="220px" rounded="md" style={{ marginTop: 'var(--space-4)' }} />
              <Skeleton.Line width="90%" style={{ marginTop: 'var(--space-4)' }} />
              <Skeleton.Line width="80%" />
              <Skeleton.Line width="65%" />
              <Skeleton.Block height="48px" rounded="pill" style={{ marginTop: 'var(--space-5)', maxWidth: '320px' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail">
        <div className="container">
          <div className="error-message">
            <h2>{t('productDetail.notFound', 'Product Not Found')}</h2>
            <p>{t('productDetail.notFoundDesc', 'The product you are looking for could not be found.')}</p>
            <Link to="/products" className="btn btn-primary" style={{ marginTop: 'var(--space-lg)' }}>
              {t('productDetail.backToProducts', 'Back to Products')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const productAttributes = product.attributes || [];
  const productVariants = product.variants || [];
  const hasAttributes = productAttributes.length > 0;

  const matchedVariant = hasAttributes && Object.keys(selectedOptions).length > 0
    ? productVariants.find((v) => {
        const opts = v.options || {};
        return productAttributes.every((attr) => {
          if (!attr.name || !selectedOptions[attr.name]) return true;
          return opts[attr.name] === selectedOptions[attr.name];
        });
      })
    : null;

  const effectivePrice = matchedVariant?.price ?? product.price;
  const effectiveStock = matchedVariant?.stock ?? null;
  const effectiveStockStatus = effectiveStock !== null
    ? (effectiveStock > 5 ? 'in_stock' : effectiveStock > 0 ? 'low_stock' : 'out_of_stock')
    : product.stockStatus || 'out_of_stock';
  const effectiveSku = matchedVariant?.sku ?? product.sku;
  const variantImage = matchedVariant?.image || null;

  const stockStatus = getStockDisplay(effectiveStockStatus);
  const currentMedia = media[selectedIndex] || null;

  const allOptionsSelected = productAttributes.every((attr) =>
    attr.type === 'text' || selectedOptions[attr.name]
  );
  const variantDisabled = hasAttributes && (!allOptionsSelected || (matchedVariant && !matchedVariant.active));

  const templateSections = resourceTemplate?.template?.sections || [
    { type: 'product_main', enabled: true },
    { type: 'product_related', enabled: true },
    { type: 'product_recent', enabled: true },
    { type: 'product_reviews', enabled: true },
  ];
  const templateRegion = (type) => {
    const position = templateSections.findIndex((section) => section.type === type);
    const section = position >= 0 ? templateSections[position] : null;
    return { enabled: section?.enabled !== false && section !== null, order: position >= 0 ? position : 99 };
  };
  const mainRegion = templateRegion('product_main');
  const relatedRegion = templateRegion('product_related');
  const recentRegion = templateRegion('product_recent');
  const reviewsRegion = templateRegion('product_reviews');
  const templateContentSections = templateSections.filter((section) => HOME_SECTION_REGISTRY[section.type] && section.enabled !== false);

  return (
    <div className="product-detail">
      <SEO
        // Meta fields are single-language; outside English the localized name
        // and description win, as in the server-rendered head.
        title={(i18n.language !== 'en' ? null : product.metaTitle) || product.name}
        description={(i18n.language !== 'en' ? null : product.metaDescription) || product.description}
        keywords={product.metaKeywords || [product.name]}
        image={product.images?.[0] ? getImageUrl(product.images[0], { size: 'large' }) : null}
        type="product"
        price={product.price}
        currency={ctxCurrency || 'USD'}
        stockStatus={product.stockStatus}
        sku={product.sku}
        brand={storeName}
        averageRating={product.averageRating}
        reviewCount={product.reviewCount}
        reviews={reviews}
        breadcrumbs={[
          { name: t('nav.home', 'Home'), url: '/' },
          { name: t('nav.products', 'Products'), url: '/products' },
          ...(product.category?.slug
            ? [{ name: product.category.name, url: `/products?category=${product.category.slug}` }]
            : []),
          { name: product.name, url: `/products/${product.slug}` },
        ]}
      />

      <div className="container product-template-regions" data-template={product.themeTemplateKey || 'product.default'}>
        <nav className="breadcrumb" style={{ order: -1 }}>
          <Link to="/">{t('nav.home', 'Home')}</Link>
          <span className="breadcrumb-sep">&rsaquo;</span>
          <Link to="/products">{t('nav.products', 'Products')}</Link>
          <span className="breadcrumb-sep">&rsaquo;</span>
          <span className="breadcrumb-current">{product.name}</span>
        </nav>

        <div className="product-detail-layout" style={{ display: mainRegion.enabled ? undefined : 'none', order: mainRegion.order }}>
          {/* Gallery / Carousel */}
          <div className="product-gallery">
            <div
              className={`gallery-main${media.length === 0 ? ' no-media' : ''}`}
              onClick={() => media.length > 0 && setLightboxOpen(true)}
            >
              {media.length > 0 ? (
                <div className="gallery-media-stack">
                  {media.map((item, idx) => (
                    <div
                      key={idx}
                      className={`gallery-stack-item ${idx === selectedIndex ? 'active' : ''}`}
                    >
                      {item === product.animationUrl ? (
                        <video
                          src={getImageUrl(item)}
                          poster={product.images?.[0] ? getImageUrl(product.images[0], { size: 'large' }) : undefined}
                          autoPlay
                          muted
                          loop
                          playsInline
                          aria-label={product.name}
                          className="gallery-main-media"
                        />
                      ) : isVideo(item) ? (
                        <video
                          src={getImageUrl(item)}
                          controls
                          playsInline
                          className="gallery-main-media"
                        />
                      ) : (
                        <img
                          src={getImageUrl(item, { size: 'large' })}
                          alt={`${product.name} — ${idx + 1} of ${media.length}`}
                          className="gallery-main-media"
                          onLoad={fitToFrame}
                          onError={(e) => { if (!e.target.dataset.fallback) { e.target.dataset.fallback = '1'; e.target.src = getImageUrl(item); } }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gallery-placeholder">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                  <p>{t('productDetail.noImage', 'No image available')}</p>
                </div>
              )}

              {media.length > 1 && (
                <>
                  <button
                    className="gallery-arrow gallery-arrow-left"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    aria-label="Previous"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    className="gallery-arrow gallery-arrow-right"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    aria-label="Next"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                  <div className="gallery-counter">
                    {selectedIndex + 1} / {media.length}
                  </div>
                </>
              )}
            </div>

            {media.length > 1 && (
              <div className="gallery-thumbs">
                {media.map((item, idx) => (
                  <button
                    key={idx}
                    className={`gallery-thumb ${selectedIndex === idx ? 'active' : ''}`}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    {isVideo(item) ? (
                      <>
                        {product.images?.[0] && !isVideo(product.images[0]) && (
                          <img src={getImageUrl(product.images[0], { size: 'thumb' })} alt="" />
                        )}
                        <span className="thumb-video-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                      </>
                    ) : (
                      <img
                        src={getImageUrl(item, { size: 'thumb' })}
                        alt={`${product.name} — ${idx + 1} of ${media.length}`}
                        onError={(e) => { if (!e.target.dataset.fallback) { e.target.dataset.fallback = '1'; e.target.src = getImageUrl(item); } }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-details">
            {product.category && (
              <Link
                to={`/products?category=${product.category.slug}`}
                className="product-category-badge"
              >
                {product.category.name}
              </Link>
            )}

            <h1 className="product-title">{product.name}</h1>

            <div className="product-social-row">
              <SaveButton productId={product.id} size="md" />
              <ShareButton
                path={`/products/${product.slug}`}
                title={product.name}
                text={`${formatPrice(effectivePrice, { lang: i18n.language })} ${currencyLabel || ''}`.trim()}
                itemId={product.sku || String(product.id)}
              />
            </div>

            {effectiveSku && (
              <span className="product-sku">SKU: {effectiveSku}</span>
            )}

            <div className="product-price-section">
              <div className="price-label">{t('productDetail.price', 'Price')}</div>
              <div className="product-price-row">
                <span className="product-price-large">
                  {formatPrice(effectivePrice, { lang: i18n.language })} {currencyLabel || t('common.currency')}
                </span>
                <Badge variant="success" className="ce-badge" aria-label={t('productDetail.qualityValue', 'Verified Quality & Safety')}>
                  <Icon.Secure size={12} strokeWidth={2.25} aria-hidden="true" style={{ marginRight: 4 }} />
                  {t('productDetail.quality', 'Verified Quality')}
                </Badge>
                {featureAgeRecommendations && (product.ageMin != null || product.ageMax != null) && (
                  <Badge variant="neutral" aria-label={t('pdp.ageRangeLabel', 'Recommended age')}>
                    {ageLabel(t, product)}
                  </Badge>
                )}
              </div>
              {product.stockStatus !== 'out_of_stock' && (
                <IuteInstalments amount={effectivePrice} sku={iuteSku(product)} pageType="product" className="product-instalments" />
              )}
              <div className={`stock-status ${stockStatus.className}`}>
                {stockStatus.text}
              </div>
            </div>

            {/* Product Option Selectors */}
            {hasAttributes && (
              <div className="product-options" style={{ marginBottom: '1rem' }}>
                {productAttributes.map((attr) => {
                  if (attr.type === 'text') {
                    return (
                      <div key={attr.name} style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginRight: '0.5rem' }}>{attr.name}:</span>
                        {(attr.values || []).map((v, i) => (
                          <span key={i} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '6px', fontSize: '0.813rem', color: '#475569', marginRight: '6px' }}>
                            {typeof v === 'object' ? v.label : v}
                          </span>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <div key={attr.name} style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                        {attr.name}: <span style={{ fontWeight: 400, color: '#6b7280' }}>{selectedOptions[attr.name] || 'Select'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {(attr.values || []).map((val, valIdx) => {
                          const label = attr.type === 'color' && typeof val === 'object' ? val.label : val;
                          const hex = attr.type === 'color' && typeof val === 'object' ? val.hex : null;
                          const isSelected = selectedOptions[attr.name] === label;

                          if (attr.type === 'color' && hex) {
                            return (
                              <button key={valIdx} type="button" title={label}
                                onClick={() => setSelectedOptions((prev) => ({ ...prev, [attr.name]: label }))}
                                style={{
                                  width: '36px', height: '36px', borderRadius: '50%', border: isSelected ? '3px solid #8B5CF6' : '2px solid #d1d5db',
                                  background: hex, cursor: 'pointer', outline: isSelected ? '2px solid #c4b5fd' : 'none', outlineOffset: '2px',
                                  transition: 'all 0.15s',
                                }}
                              />
                            );
                          }

                          return (
                            <button key={valIdx} type="button"
                              onClick={() => setSelectedOptions((prev) => ({ ...prev, [attr.name]: label }))}
                              style={{
                                padding: '8px 16px', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                                border: isSelected ? '2px solid #8B5CF6' : '1px solid #d1d5db',
                                background: isSelected ? '#f5f3ff' : 'white', color: isSelected ? '#7c3aed' : '#374151',
                                transition: 'all 0.15s',
                              }}
                            >{label}</button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {product.availabilityStatus === 'coming_soon' ? (
              <div className="product-actions" style={{ flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderRadius: '12px', padding: '1rem',
                  border: '1px solid #fcd34d', textAlign: 'center',
                }}>
                  <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#92400e', fontSize: '0.938rem' }}>
                    {t('productDetail.comingSoon', 'Coming Soon')}
                  </p>
                  <p style={{ margin: 0, color: '#78350f', fontSize: '0.813rem' }}>
                    {t('productDetail.notifyMeDesc', 'Get notified when this product becomes available.')}
                  </p>
                </div>
                {waitlistMessage ? (
                  <div style={{
                    padding: '0.75rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', textAlign: 'center',
                    background: waitlistMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
                    color: waitlistMessage.type === 'success' ? '#065f46' : '#991b1b',
                    border: `1px solid ${waitlistMessage.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                  }}>
                    {waitlistMessage.text}
                  </div>
                ) : (
                  <form onSubmit={handleWaitlistSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="email"
                      value={waitlistEmail}
                      onChange={e => setWaitlistEmail(e.target.value)}
                      placeholder={t('productDetail.yourEmail', 'Your email address')}
                      required
                      style={{ flex: 1, padding: '0.625rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem' }}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={waitlistSubmitting}
                      style={{ whiteSpace: 'nowrap', opacity: waitlistSubmitting ? 0.5 : 1 }}
                    >
                      {waitlistSubmitting ? '...' : t('productDetail.notifyMe', 'Notify Me')}
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <div className="product-actions">
                <div className="quantity-selector">
                  <label htmlFor="quantity">{t('productDetail.quantity', 'Quantity')}:</label>
                  <div className="quantity-controls">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="quantity-btn"
                      disabled={quantity <= 1}
                      aria-label={t('productDetail.decreaseQuantity', 'Decrease quantity')}
                    >-</button>
                    <input
                      id="quantity"
                      type="number"
                      min="1"
                      max={maxQty}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(maxQty, parseInt(e.target.value) || 1)))}
                      className="quantity-input"
                      aria-label={t('productDetail.quantity', 'Quantity')}
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.min(maxQty, quantity + 1))}
                      className="quantity-btn"
                      disabled={quantity >= maxQty}
                      aria-label={t('productDetail.increaseQuantity', 'Increase quantity')}
                    >+</button>
                  </div>
                </div>
                <button
                  ref={primaryCtaRef}
                  className="btn btn-primary btn-add-to-cart"
                  onClick={() => addToCart(product, quantity, matchedVariant ? { id: matchedVariant.id, options: selectedOptions, price: effectivePrice, sku: effectiveSku, image: variantImage } : null)}
                  disabled={effectiveStockStatus === 'out_of_stock' || variantDisabled}
                >
                  {hasAttributes && !allOptionsSelected
                    ? t('productDetail.selectOptions', 'Select Options')
                    : t('productDetail.addToCart', 'Add to Cart')}
                </button>
                {effectiveStockStatus !== 'out_of_stock' && allOptionsSelected && (
                  <button
                    type="button"
                    className="pdp__buy-now-btn"
                    onClick={() => {
                      addToCart(product, quantity, matchedVariant ? { id: matchedVariant.id, options: selectedOptions, price: effectivePrice, sku: effectiveSku, image: variantImage } : null, { skipSidebar: true, skipToast: true });
                      navigate('/checkout');
                    }}
                  >
                    {t('productDetail.buyNow', 'Buy Now')}
                  </button>
                )}
              </div>
            )}

            {product.description && (
              <div className="product-section">
                <h3 className="section-heading">{t('productDetail.description', 'Description')}</h3>
                <p className="product-description">{product.description}</p>
              </div>
            )}

            {(() => {
              const specs = product.specifications;
              if (!specs) return null;

              if (typeof specs === 'string' && specs.trim()) {
                return (
                  <div className="product-section">
                    <h3 className="section-heading">{t('pdp.specifications', 'Specifications')}</h3>
                    <p className="product-description">{specs}</p>
                  </div>
                );
              }

              if (Array.isArray(specs) && specs.length > 0) {
                return (
                  <div className="product-section">
                    <h3 className="section-heading">{t('pdp.specifications', 'Specifications')}</h3>
                    <ul className="product-specs__list">
                      {specs.map((s, i) => (
                        <li key={i}>{typeof s === 'string' ? s : `${s.label || s.name || ''}: ${s.value || ''}`}</li>
                      ))}
                    </ul>
                  </div>
                );
              }

              if (specs && typeof specs === 'object' && !Array.isArray(specs)) {
                const entries = Object.entries(specs).filter(([, v]) => v !== null && v !== '');
                if (entries.length === 0) return null;

                const humanizeKey = (raw) => {
                  const key = String(raw).toLowerCase().trim();
                  const translated = t(`pdp.specKeys.${key}`, { defaultValue: '__missing__' });
                  if (translated !== '__missing__') return translated;
                  return raw
                    .toString()
                    .replace(/[_-]+/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase());
                };

                const formatValue = (value) => {
                  if (typeof value === 'boolean') return value ? t('common.yes', 'Yes') : t('common.no', 'No');
                  if (typeof value === 'number') return value.toString();
                  if (Array.isArray(value)) return value.join(', ');
                  return String(value);
                };

                return (
                  <div className="product-section">
                    <h3 className="section-heading">{t('pdp.specifications', 'Specifications')}</h3>
                    <dl className="product-specs__grid">
                      {entries.map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt>{humanizeKey(k)}</dt>
                          <dd>{formatValue(v)}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                );
              }

              return null;
            })()}

            {product.tags && product.tags.length > 0 && (
              <div className="product-section">
                <h3 className="section-heading">{t('productDetail.features', 'Features')}</h3>
                <div className="product-tags-list">
                  {product.tags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/products?tags=${tag}`}
                      className="product-tag-chip"
                    >
                      {t(`tags.${tag}`, tag.charAt(0).toUpperCase() + tag.slice(1).replace(/-/g, ' '))}
                    </Link>
                  ))}
                </div>
              </div>
            )}


            <div className="product-info-cards">
              <div className="info-card">
                <Icon.Shipping
                  className="info-card-icon"
                  size={22}
                  strokeWidth={ICON_DEFAULTS.strokeWidth}
                  aria-hidden="true"
                />
                <div className="info-card-label">{t('productDetail.shipping', 'Shipping')}</div>
                <div className="info-card-value">
                  {(() => {
                    const cur = currencyLabel || t('common.currency');
                    return parseFloat(siteSettings.freeShippingThreshold) > 0
                      ? t('common.freeShippingOver', { amount: formatPrice(siteSettings.freeShippingThreshold, { lang: i18n.language }), currency: cur })
                      : `${formatPrice(siteSettings.shippingCost, { lang: i18n.language })} ${cur}`;
                  })()}
                </div>
              </div>
              <div className="info-card">
                <Icon.Secure
                  className="info-card-icon"
                  size={22}
                  strokeWidth={ICON_DEFAULTS.strokeWidth}
                  aria-hidden="true"
                />
                <div className="info-card-label">{t('productDetail.quality', 'Verified Quality')}</div>
                <div className="info-card-value">{t('productDetail.qualityValue', 'Verified Quality & Safety')}</div>
              </div>
              {returnsEnabled && (
                <div className="info-card">
                  <Icon.Returns
                    className="info-card-icon"
                    size={22}
                    strokeWidth={ICON_DEFAULTS.strokeWidth}
                    aria-hidden="true"
                  />
                  <div className="info-card-label">{t('productDetail.returnsLabel', 'Returns')}</div>
                  <div className="info-card-value">
                    {t('productDetail.returnsValue', { days: returnsWindowDays, defaultValue: `${returnsWindowDays} days` })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {templateContentSections.map((section) => <div key={section.key} style={{ order: section.position ?? templateSections.indexOf(section) }}><SectionRenderer layout={{ template: { sections: [section] } }} registry={HOME_SECTION_REGISTRY} context={{ isEditorPreview, dynamicSources: { product, collection: product.category || {}, store: { name: storeName, email: storeEmail, phone: storePhone, address: storeAddress, nipt: storeNipt, whatsAppPhone, currency: ctxCurrency } } }} /></div>)}

        {relatedRegion.enabled && relatedProducts.length > 0 && (
          <section className="related-section" style={{ order: relatedRegion.order }}>
            <h2 className="related-heading">{t('productDetail.relatedProducts', 'You May Also Like')}</h2>
            <div className="related-grid">
              {relatedProducts.map((rp) => (
                <ProductCard
                  key={rp.id}
                  product={rp}
                  variant="compact"
                  showActions={false}
                  showBadges={false}
                  showDescription={false}
                  showCategory={false}
                />
              ))}
            </div>
          </section>
        )}

        {recentRegion.enabled && recentlyViewed.length > 0 && (
          <section className="related-section" style={{ order: recentRegion.order }}>
            <h2 className="related-heading">{t('productDetail.recentlyViewed', 'Recently Viewed')}</h2>
            <div className="related-grid">
              {recentlyViewed.map((rv) => (
                <ProductCard
                  key={rv.id}
                  product={rv}
                  variant="compact"
                  showActions={false}
                  showBadges={false}
                  showDescription={false}
                  showCategory={false}
                />
              ))}
            </div>
          </section>
        )}

        {reviewsRegion.enabled && <section className="reviews-section" aria-labelledby="reviews-heading" style={{ order: reviewsRegion.order }}>
          <header className="reviews-header">
            <div className="reviews-header__intro">
              <h2 id="reviews-heading" className="reviews-heading">
                {t('pdp.reviews', 'Reviews')}
              </h2>
              {reviewStats.total > 0 ? (
                <div className="reviews-summary" aria-label={t('pdp.reviewsAverage', { rating: reviewStats.averageRating })}>
                  <span className="reviews-summary__stars" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Icon.Star
                        key={n}
                        size={16}
                        strokeWidth={1.5}
                        className={n <= Math.round(reviewStats.averageRating) ? 'is-filled' : ''}
                      />
                    ))}
                  </span>
                  <span className="reviews-summary__value">{reviewStats.averageRating}</span>
                  <span className="reviews-summary__count">
                    ({t('pdp.reviewsCount', { count: reviewStats.total })})
                  </span>
                </div>
              ) : (
                <p className="reviews-empty">{t('pdp.noReviews', 'No reviews yet — be the first to share your experience.')}</p>
              )}
            </div>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {t('pdp.writeReview', 'Write a review')}
            </Button>
          </header>

          {showReviewForm && (
            <form className="review-form" onSubmit={handleSubmitReview}>
              <div className="review-form__row">
                <div className="review-form__field">
                  <label htmlFor="review-name">{t('productDetail.yourName', 'Your Name')} *</label>
                  <input
                    id="review-name"
                    type="text"
                    value={reviewForm.authorName}
                    onChange={(e) => setReviewForm((f) => ({ ...f, authorName: e.target.value }))}
                    required
                  />
                </div>
                <div className="review-form__field">
                  <label htmlFor="review-rating">{t('pdp.rating', 'Rating')}</label>
                  <div className="review-form__stars" role="radiogroup" aria-label={t('pdp.rating', 'Rating')}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={reviewForm.rating === n}
                        aria-label={t('pdp.ratingStars', { rating: n })}
                        className={`review-star ${n <= reviewForm.rating ? 'is-filled' : ''}`}
                        onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                      >
                        <Icon.Star size={22} strokeWidth={1.5} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="review-form__field">
                <label htmlFor="review-title">{t('pdp.title', 'Title')}</label>
                <input
                  id="review-title"
                  type="text"
                  value={reviewForm.title}
                  onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div className="review-form__field">
                <label htmlFor="review-body">{t('pdp.comment', 'Your review')}</label>
                <textarea
                  id="review-body"
                  rows={4}
                  value={reviewForm.body}
                  onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))}
                />
              </div>
              <div className="review-form__actions">
                <Button type="submit" variant="brand" size="md" disabled={submittingReview}>
                  {submittingReview ? t('pdp.reviewSubmitting', 'Submitting…') : t('pdp.submitReview', 'Submit review')}
                </Button>
                <Button variant="secondary" size="md" onClick={() => setShowReviewForm(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
              </div>
            </form>
          )}

          {reviews.length > 0 && (
            <ul className="reviews-list">
              {reviews.map((r) => (
                <li key={r.id} className="review-card">
                  <div className="review-card__head">
                    <span className="review-card__stars" aria-label={t('pdp.ratingStars', { rating: r.rating })}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Icon.Star
                          key={n}
                          size={14}
                          strokeWidth={1.5}
                          className={n <= r.rating ? 'is-filled' : ''}
                        />
                      ))}
                    </span>
                    <span className="review-card__author">{r.authorName}</span>
                    <span className="review-card__date">{r.createdAt}</span>
                  </div>
                  {r.title && <p className="review-card__title">{r.title}</p>}
                  {r.body && <p className="review-card__body">{r.body}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>}

        <div className="product-back-strip" style={{ order: 100 }}>
          <Link to="/products" className="btn btn-outline">
            {t('productDetail.browseMore', 'Browse More Products')}
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && currentMedia && (
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            {isVideo(currentMedia) ? (
              <video src={getImageUrl(currentMedia)} controls autoPlay playsInline className="lightbox-media" />
            ) : (
              <img src={getImageUrl(currentMedia)} alt={product.name} className="lightbox-media" />
            )}
            {media.length > 1 && (
              <>
                <button className="lightbox-arrow lightbox-arrow-left" onClick={goPrev}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button className="lightbox-arrow lightbox-arrow-right" onClick={goNext}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <div className="lightbox-counter">{selectedIndex + 1} / {media.length}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile-only sticky buy bar — appears once the primary CTA
          scrolls out of view. The desktop CTA stays the source of truth;
          this bar mirrors its handler and disabled state. */}
      {product && stickyBarVisible && (
        <div
          className={`pdp-sticky-bar${effectiveStockStatus === 'out_of_stock' ? ' is-disabled' : ''}`}
          role="region"
          aria-label={t('productDetail.stickyBar', 'Quick add to cart')}
        >
          <div className="pdp-sticky-bar__inner">
            <div className="pdp-sticky-bar__meta">
              <span className="pdp-sticky-bar__name" title={product.name}>{product.name}</span>
              {Object.keys(selectedOptions).length > 0 && (
                <span className="pdp-sticky-bar__variant">
                  {Object.values(selectedOptions).join(' · ')}
                </span>
              )}
              <span className="pdp-sticky-bar__price">
                {formatPrice(effectivePrice, { lang: i18n.language })} {currencyLabel || t('common.currency')}
              </span>
            </div>
            {effectiveStockStatus === 'out_of_stock' ? (
              <span className="pdp-sticky-bar__oos">{t('productDetail.outOfStock', 'Out of Stock')}</span>
            ) : (
              <button
                type="button"
                className="pdp-sticky-bar__cta"
                onClick={() => addToCart(product, quantity, matchedVariant ? { id: matchedVariant.id, options: selectedOptions, price: effectivePrice, sku: effectiveSku, image: variantImage } : null)}
                disabled={(hasAttributes && !allOptionsSelected) || variantDisabled}
              >
                {hasAttributes && !allOptionsSelected
                  ? t('productDetail.selectOptions', 'Select Options')
                  : t('productDetail.addToCart', 'Add to Cart')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
